import React, { useState } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { Property } from '../../types';
import LotAvailabilityBadge from './LotAvailabilityBadge';
import {
  CATALOG_PROGRAM_CHIP_BASE,
  programSecondaryBadgeClass,
  projectStatusBadgeClass,
  propertyProgramCategoryLabel,
} from '../../utils/programLabels';
import { resolveAvanceeCatalogBannerLabel } from '../../utils/vitrineAvanceeTimeline';

interface ProgramInfo {
  name: string;
  location: string;
  image: string;
  deliveryDate: string;
}

function formatProgramLocationLine(location: string): string {
  const raw = String(location || '').trim();
  if (!raw) return '…';
  const parts = raw.split(/\s*·\s*/).filter(Boolean);
  if (parts.length >= 2) return parts.join(' · ');
  return raw.replace(/\s*-\s*/g, ' · ');
}

function goToProgramDetail(
  onClick: (() => void) | undefined,
  propertyId: string | undefined,
  navigate: NavigateFunction,
) {
  if (onClick) {
    onClick();
  } else if (propertyId) {
    navigate(`/projet/${propertyId}`);
  }
}

interface PropertyCardProps {
  property: Property;
  variant?: 'default' | 'lot' | 'program';
  program?: ProgramInfo;
  lots?: Property[];
  onClick?: () => void;
  onLotClick?: (lot: Property) => void;
  className?: string;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  variant = 'lot',
  program,
  lots = [],
  onClick,
  onLotClick,
  className = '',
}) => {
  const navigate = useNavigate();
  const [showAllLots, setShowAllLots] = useState(false);
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price).replace('€', '€').replace(/\s/g, ' ');
  };

  // Générer le type de logement (T2, T3, etc.)
  const getPropertyTypeLabel = () => {
    if (property.rooms) {
      return `T${property.rooms} - ${property.surface}m²`;
    }
    return `${property.surface}m²`;
  };

  const getFloor = () => property.lotFloor || '…';

  const getOrientation = () => property.lotOrientation || '…';

  const getExterior = () => property.lotExterior || '…';

  const hasParking =
    property.lotParkingIncluded === true ||
    property.features.some(
      (f) => f.toLowerCase().includes('parking') || f.toLowerCase().includes('garage'),
    );

  // Variante "program" : Programme avec ses lots
  if (variant === 'program' && program) {
    const promoLabels = (property.tags || []).map((t) => String(t).trim()).filter(Boolean);
    const atoutLabels = (property.features || []).map((f) => String(f).trim()).filter(Boolean);
    const avanceeLabel = resolveAvanceeCatalogBannerLabel(
      property.avanceeTimeline,
      program.deliveryDate || property.availability,
    );
    const showBannerRow =
      Boolean(avanceeLabel) || promoLabels.length > 0 || atoutLabels.length > 0;

    return (
      <div
        className={`catalog-program-card group/program relative overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm motion-safe:transition-[box-shadow,transform] motion-safe:duration-300 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md ${className}`}
      >
        {showBannerRow ? (
          <div className="catalog-program-card__chips flex flex-wrap items-center gap-2 border-b border-neutral-100 bg-neutral-50/80 px-4 py-3 sm:px-5">
            {avanceeLabel ? (
              <span
                className={`${CATALOG_PROGRAM_CHIP_BASE} ${projectStatusBadgeClass('en-cours')}`}
              >
                {avanceeLabel}
              </span>
            ) : null}
            {promoLabels.map((label) => (
              <span
                key={`promo-${label}`}
                className={`${CATALOG_PROGRAM_CHIP_BASE} ${programSecondaryBadgeClass()}`}
              >
                {label}
              </span>
            ))}
            {atoutLabels.map((label) => (
              <span key={`atout-${label}`} className="catalog-program-chip--atout">
                {label}
              </span>
            ))}
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,17.5rem)_1fr] lg:gap-0">
          <aside className="catalog-program-card__aside border-b border-neutral-100 p-4 sm:p-5 lg:border-b-0 lg:border-r lg:border-neutral-100">
            <div className="catalog-program-card__media relative mb-4 overflow-hidden rounded-md bg-neutral-100">
              <img
                src={
                  program.image ||
                  'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
                }
                alt={program.name}
                className="aspect-[4/3] h-full w-full object-cover transition-transform duration-500 group-hover/program:scale-[1.03]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>

            <h3 className="font-display text-sm font-bold uppercase leading-snug tracking-wide text-neutral-900 sm:text-[0.9375rem]">
              {program.name}
            </h3>

            <div className="mt-2.5 flex items-start gap-1.5 text-neutral-600">
              <svg
                className="mt-0.5 h-4 w-4 shrink-0 text-teal-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span className="text-xs leading-relaxed">
                {formatProgramLocationLine(program.location)}
              </span>
            </div>

            <p className="mt-2 text-xs text-neutral-500">
              Livraison : <span className="font-medium text-neutral-700">{program.deliveryDate}</span>
            </p>

            <button
              type="button"
              onClick={() => goToProgramDetail(onClick, property.id, navigate)}
              className="catalog-program-card__detail-btn mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md border px-3 py-2.5 text-xs font-semibold transition-colors sm:w-auto"
            >
              Détail du projet
              <svg
                className="h-3.5 w-3.5 shrink-0 transition-transform group-hover/program:translate-x-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </aside>

          <div className="catalog-program-card__lots min-w-0 p-4 sm:p-5">
            <h4 className="font-display text-base font-semibold tracking-tight text-neutral-900 sm:text-lg">
              {lots.length === 0
                ? 'Aucun lot disponible ou en option pour ce programme'
                : `${lots.length} lot${lots.length > 1 ? 's' : ''} disponible${lots.length > 1 ? 's' : ''} ou en option dans ce programme`}
            </h4>
            <p className="mt-1 mb-4 text-xs text-neutral-500">
              Sélectionnez un lot pour voir le détail et le plan
            </p>
            <div className="space-y-2.5">
              {lots.length === 0 ? (
                <p className="rounded-lg border border-dashed border-neutral-200 bg-white px-4 py-6 text-center text-sm text-neutral-500">
                  Ajoutez et enregistrez des lots en commercialisation (statut Disponible ou Option) dans le logiciel Prospection.
                </p>
              ) : null}
              {(showAllLots ? lots : lots.slice(0, 3)).map((lot) => (
                <PropertyCard
                  key={lot.id}
                  property={lot}
                  variant="lot"
                  onClick={() => onLotClick?.(lot)}
                />
              ))}
            </div>
            {lots.length > 3 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllLots(!showAllLots);
                }}
                className="catalog-program-card__more-btn mt-4 inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-semibold transition-colors"
              >
                {showAllLots ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                    Voir moins de lots
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Voir plus de lots
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Variante "lot" — ligne catalogue
  return (
    <div
      className={`group/lot catalog-lot-row flex flex-col gap-0 overflow-hidden rounded-md border border-neutral-200 bg-white motion-safe:transition-[transform,box-shadow,border-color] motion-safe:duration-300 motion-safe:hover:border-neutral-300 motion-safe:hover:shadow-sm sm:flex-row sm:items-stretch ${className}`}
      onClick={onClick}
    >
      <div className="catalog-lot-row__grid grid min-w-0 flex-1 grid-cols-2 gap-x-4 gap-y-3 p-3.5 sm:grid-cols-[minmax(5.5rem,1fr)_repeat(4,minmax(2.5rem,1fr))_minmax(6.5rem,auto)] sm:items-center sm:gap-x-3 sm:p-4 lg:gap-x-4">
        <div className="col-span-2 flex flex-col justify-center sm:col-span-1">
          <p className="text-sm font-bold leading-tight tracking-tight text-neutral-900">
            {getPropertyTypeLabel()}
          </p>
          <p className="catalog-lot-row__category mt-1.5 inline-flex w-fit max-w-full rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
            {propertyProgramCategoryLabel(property.type)}
          </p>
        </div>

        <div className="catalog-lot-row__cell flex flex-col justify-center">
          <p className="catalog-lot-row__label">Étage</p>
          <p className="catalog-lot-row__value tabular-nums">{getFloor()}</p>
        </div>

        <div className="catalog-lot-row__cell flex flex-col justify-center">
          <p className="catalog-lot-row__label">Face</p>
          <p className="catalog-lot-row__value">{getOrientation()}</p>
        </div>

        <div className="catalog-lot-row__cell flex flex-col justify-center">
          <p className="catalog-lot-row__label">Ext.</p>
          <p className="catalog-lot-row__value">{getExterior()}</p>
        </div>

        {property.lotAvailabilityStatus ? (
          <div className="catalog-lot-row__cell flex flex-col justify-center">
            <p className="catalog-lot-row__label">Statut</p>
            <div className="mt-1">
              <LotAvailabilityBadge status={property.lotAvailabilityStatus} />
            </div>
          </div>
        ) : (
          <div className="catalog-lot-row__cell hidden flex-col justify-center sm:flex">
            <p className="catalog-lot-row__label">Statut</p>
            <p className="catalog-lot-row__value text-neutral-400">…</p>
          </div>
        )}

        <div className="catalog-lot-row__price col-span-2 flex flex-col justify-center sm:col-span-1">
          <p className="catalog-lot-row__label">Prix</p>
          <p className="catalog-lot-row__price-value whitespace-nowrap tabular-nums">
            {formatPrice(property.price)}
          </p>
          {hasParking ? (
            <p className="mt-1 text-[10px] font-medium leading-none text-neutral-500">Parking inclus</p>
          ) : null}
        </div>
      </div>

      <div className="catalog-lot-row__action flex shrink-0 items-center border-t border-neutral-100 p-3 sm:border-l sm:border-t-0 sm:px-4 sm:py-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onLotClick) {
              onLotClick(property);
            } else if (onClick) {
              onClick();
            }
          }}
          className="catalog-lot-row__btn w-full whitespace-nowrap rounded-md px-4 py-2.5 text-xs font-semibold transition-all duration-200 active:scale-[0.98] sm:min-w-[8.5rem]"
        >
          Détail et plan
        </button>
      </div>
    </div>
  );
};

export default PropertyCard;

